import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";

/**
 * Textarea/input with @mention autocomplete. Detects the "@word" token at the
 * caret, fetches matching users, and inserts "@username " on pick.
 * Fully controlled: pass value + onChange(nextString).
 */
export default function MentionTextarea({
    value,
    onChange,
    placeholder = "",
    rows = 2,
    maxLength = 5000,
    className = "",
    testid = "mention-textarea",
    as = "textarea",
    onKeyDown,
    wrapperClass = "relative w-full",
    autoGrow = false,
    maxGrowPx = 200,
}) {
    const ref = useRef(null);
    const boxRef = useRef(null);
    const [open, setOpen] = useState(false);
    const [items, setItems] = useState([]);
    const [active, setActive] = useState(0);
    const [token, setToken] = useState(null); // { start, query }
    const debounce = useRef(null);

    const findToken = (el) => {
        const pos = el.selectionStart ?? value.length;
        const upto = value.slice(0, pos);
        const m = upto.match(/(?:^|[^\w@.])@([A-Za-z0-9_.]*)$/);
        if (!m) return null;
        return { start: pos - m[1].length - 1, query: m[1] };
    };

    const refresh = (el) => {
        const t = findToken(el);
        setToken(t);
        if (!t) { setOpen(false); return; }
        clearTimeout(debounce.current);
        debounce.current = setTimeout(async () => {
            try {
                const { data } = await api.get(`/social/mention-search?q=${encodeURIComponent(t.query)}`);
                setItems(data.items || []);
                setActive(0);
                setOpen((data.items || []).length > 0);
            } catch { setOpen(false); }
        }, 150);
    };

    const pick = (u) => {
        if (!token) return;
        const before = value.slice(0, token.start);
        const after = value.slice(token.start + 1 + token.query.length);
        const next = `${before}@${u.username} ${after.replace(/^\s/, "")}`;
        onChange(next);
        setOpen(false);
        requestAnimationFrame(() => {
            const el = ref.current;
            if (el) {
                const caret = before.length + u.username.length + 2;
                el.focus();
                el.setSelectionRange(caret, caret);
            }
        });
    };

    useEffect(() => {
        const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target) && ref.current !== e.target) setOpen(false); };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, []);

    // Auto-grow the textarea so wrapped text stays fully visible before sending.
    useEffect(() => {
        if (autoGrow && as === "textarea" && ref.current) {
            ref.current.style.height = "auto";
            ref.current.style.height = `${Math.min(ref.current.scrollHeight, maxGrowPx)}px`;
        }
    }, [value, autoGrow, as, maxGrowPx]);

    const handleKey = (e) => {
        if (open && items.length) {
            if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => (a + 1) % items.length); return; }
            if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => (a - 1 + items.length) % items.length); return; }
            if (e.key === "Enter" || e.key === "Tab") { e.preventDefault(); pick(items[active]); return; }
            if (e.key === "Escape") { setOpen(false); return; }
        }
        onKeyDown?.(e);
    };

    const commonProps = {
        ref,
        "data-testid": testid,
        value,
        placeholder,
        maxLength,
        onChange: (e) => { onChange(e.target.value); refresh(e.target); },
        onKeyUp: (e) => refresh(e.target),
        onClick: (e) => refresh(e.target),
        onKeyDown: handleKey,
        className,
    };

    return (
        <div className={wrapperClass} ref={boxRef}>
            {as === "input"
                ? <input {...commonProps} />
                : <textarea {...commonProps} rows={rows} />}
            {open && items.length > 0 && (
                <div className="absolute z-50 mt-1 w-64 max-h-64 overflow-y-auto border border-[#2a2a2a] bg-[#0f0f0f] shadow-2xl" data-testid={`${testid}-suggestions`}>
                    {items.map((u, i) => (
                        <button
                            key={u.username}
                            type="button"
                            data-testid={`${testid}-suggestion-${u.username}`}
                            onMouseDown={(e) => { e.preventDefault(); pick(u); }}
                            onMouseEnter={() => setActive(i)}
                            className={`flex items-center gap-2 w-full px-3 py-2 text-left ${i === active ? "bg-[#1c1c1c]" : "hover:bg-[#181818]"}`}
                        >
                            {u.photo
                                ? <img src={u.photo} alt="" className="h-6 w-6 rounded-full object-cover flex-none" />
                                : <span className="h-6 w-6 rounded-full bg-[#222] flex items-center justify-center text-[10px] text-[#ff4500] flex-none">{(u.username || "?")[0].toUpperCase()}</span>}
                            <span className="min-w-0">
                                <span className="block text-sm text-white truncate">@{u.username}</span>
                                {u.name ? <span className="block text-[10px] text-[#888] truncate">{u.name}</span> : null}
                            </span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}
